// components/LifeGoalsForm.js
import React, { useState, useEffect } from 'react';
import { 
  CardContent, 
  Typography, 
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Box,
  Grid,
  Slider,
  Input
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { financialService } from '../utils/api';
import { useAppContext } from '../context/AppContext';

function LifeGoalsForm({ formData, onAddGoal, onRemoveGoal, onChange }) {
  const { state, actions } = useAppContext();
  const [open, setOpen] = useState(false);
  const [newGoal, setNewGoal] = useState({
    type: '',
    currentValue: 0,
    targetYear: 0,
    FV_goal: 0
  });
  const currentYear = new Date().getFullYear();

  const [value, setValue] = React.useState(0);

  const handleSliderChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleInputChange = (event) => {
    setValue(event.target.value === '' ? 0 : Number(event.target.value));
  };

  const handleBlur = () => {
    if (value < 0) {
      setValue(0);
    } else if (value > 100) {
      setValue(100);
    }
  };
  
  const handleOpen = () => {
    setOpen(true);
  };
  
  const handleClose = () => {
    setOpen(false);
    // Reset form when closing
    setNewGoal({
      type: '',
      currentValue: 0,
      targetYear: 0,
      FV_goal: 0
    });
  };
  
  const handleChange = (field, value) => {
    setNewGoal({ ...newGoal, [field]: value });
  };

  // Handle emergency fund months change
  const handleEmergencyFundChange = (value) => {
    onChange('emergencyFundMonths', value);
  };

  const parseNumber = (e) => {
    let value = parseInt(e.target.value.replace(/,/g, ''), 10);
    if (isNaN(value)) value = 0;
    return value;
  };
  
const handleAddGoal = async () => {

  if (!newGoal.type.trim() || newGoal.currentValue <= 0 || !newGoal.targetYear) {
    return;
  }

  const current_year = new Date().getFullYear();
  const yearsToGoal = newGoal.targetYear - current_year;

  if (yearsToGoal < 0) {
    alert('Target year must be in the future');
    return;
  }

  const inflationRate = 0.07;
  const FutureValue = newGoal.currentValue * Math.pow(1 + inflationRate, yearsToGoal);

  try {

    const response = await financialService.submitGoals(state.phoneNumber, {
      goal_type: newGoal.type,
      current_value: newGoal.currentValue,
      target_date: newGoal.targetYear,
      future_value: Math.round(FutureValue)
    });

    //Use DB id
    const savedGoal = {
      type: newGoal.type,
      currentValue: newGoal.currentValue,
      targetYear: newGoal.targetYear,
      FV_goal: Math.round(FutureValue),
      id: response.id   // <- critical change
    };

    onAddGoal(savedGoal);

  } catch (err) {
    console.error("Goal save failed", err);
    return;
  }

  setNewGoal({
    type: '',
    currentValue: 0,
    targetYear: 0,
    FV_goal: 0
  });

  setOpen(false);
};
const handleRemoveGoal = async (goal,index) => {
  if (!window.confirm('Are you sure you want to remove this goal?')) return;

  try {
    await financialService.deleteGoal(state.phoneNumber, goal.id);
    onRemoveGoal(index);
  } catch (err) {
    console.error("Delete failed", err);
  }
};
  
  const formatNumberINR = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  // Format number with commas
  const formatNumber = (amount) => {
    if (amount >= 10000000) {
      return (amount / 10000000).toFixed(2) + " Crores";
    } else if (amount >= 100000) {
      return (amount / 100000).toFixed(2) + " Lakhs";
    }
    // return amount.toLocaleString("en-IN", {
    //   currency: "INR",
    //   maximumFractionDigits: 0,
    // });
    return Intl.NumberFormat('en-IN').format(amount);
  };

  
  return (
    <CardContent>
      <Typography variant="h6" sx={{mb:2, color:'primary.main'}} gutterBottom>
        Other Life Goals
        </Typography>
      <Typography variant="body1" component="h2" gutterBottom>
        What other life goals would you like to plan for?
      </Typography>
      
      <Button 
        variant="contained" 
        color="primary" 
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{ mb: 2 }}
      >
        Add Goal
      </Button>
      
      {/* Goals List */}
      <List>
        {formData.goals && formData.goals.length > 0 ? (
          formData.goals.map((goal, index) => (
            <ListItem key={goal.id || index} divider>
              <ListItemText
                primary={goal.type}
                secondary={`₹${formatNumber(goal.FV_goal)} required in ${goal.targetYear} (Current value: ₹${formatNumber(goal.currentValue)})`}
              />
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  aria-label="delete" 
                  onClick={() => handleRemoveGoal(goal, index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 2 }}>
             Think of any large value purchases: home, car, large value expenses: children's marriage,  value experiences: bucket list travel; or any planned gifts and legacies.
          </Typography>
        )}
      </List>
      
      {/* Add Goal Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Goal</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={1}>
                <TextField
                  label="Goal Name"
                  value={newGoal.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  fullWidth
                  margin="dense"
                  helperText="e.g., Dream Home, Wedding"
                  required
                />
                <TextField
                  label="Current Value"
                  value={newGoal.currentValue ? formatNumberINR(newGoal.currentValue) : ''}
                  onChange={(e) => handleChange('currentValue', parseNumber(e))}
                  type="tel"
                  inputMode="numeric"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  helperText="Current cost of your goal in today's money"
                  required
                />
              
                <TextField
                  label="Target Year"
                  value={newGoal.targetYear || ''}
                  onChange={(e) => handleChange('targetYear', Number(e.target.value))}
                  type="tel"
                  inputMode="numeric"
                  fullWidth
                  margin="dense"
                  helperText="When do you plan to achieve this goal?"
                  inputProps={{
                    min: currentYear,
                    max: currentYear + 50
                  }}
                  required
                />
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAddGoal}
            color="primary"
            variant="contained"
            disabled={!newGoal.type.trim() || newGoal.currentValue <= 0 || !newGoal.targetYear || newGoal.targetYear < currentYear}
          >
            Add Goal
          </Button>
        </DialogActions>
      </Dialog>
    </CardContent>
  );
}

export default LifeGoalsForm;
